import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Heartbeat, CheckCircle, XCircle, Warning, ArrowClockwise,
    Database, EnvelopeOpen, Key, Lightning, Clock, PaperPlaneTilt,
} from "@phosphor-icons/react";

function StatusDot({ ok, size = 10 }) {
    return (
        <span
            className={`inline-block rounded-full ${ok ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" : "bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.8)]"}`}
            style={{ width: size, height: size }}
        />
    );
}

function Section({ icon: Icon, title, ok, subtitle, children, testid }) {
    return (
        <div className="tech-card !p-0 overflow-hidden" data-testid={testid}>
            <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-slate-800 bg-slate-900/50">
                <Icon size={18} className="text-cyan-400" weight="fill" />
                <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-slate-50 text-sm">{title}</div>
                    {subtitle && <div className="text-[11px] text-slate-500 font-mono-alt truncate">{subtitle}</div>}
                </div>
                {typeof ok === "boolean" && (
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono-alt uppercase tracking-wider ${
                        ok
                            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            : "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                    }`}>
                        {ok ? <CheckCircle size={10} weight="fill" /> : <XCircle size={10} weight="fill" />}
                        {ok ? "healthy" : "issue"}
                    </span>
                )}
            </div>
            <div className="p-4 sm:p-5">{children}</div>
        </div>
    );
}

function KV({ k, v, ok }) {
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-slate-800/60 last:border-b-0 text-xs">
            <span className="text-slate-400 font-mono-alt">{k}</span>
            <span className="flex items-center gap-2 font-mono-alt">
                {typeof ok === "boolean" && <StatusDot ok={ok} />}
                <span className={typeof ok === "boolean" ? (ok ? "text-cyan-300" : "text-rose-300") : "text-slate-300"}>
                    {String(v)}
                </span>
            </span>
        </div>
    );
}

export default function SystemHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [testAlertLoading, setTestAlertLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get("/admin/system/health");
            setData(res.data);
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Failed to fetch system health");
            toast.error("Could not reach the system-health endpoint");
        } finally { setLoading(false); }
    }, []);

    const sendTestAlert = useCallback(async () => {
        setTestAlertLoading(true);
        try {
            const res = await api.post("/admin/system/send-test-alert");
            toast.success(`Test alert emailed to ${res.data?.sent_to || "admin inbox"}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Test alert failed");
        } finally { setTestAlertLoading(false); }
    }, []);

    useEffect(() => {
        document.title = "Surveys2CAD | Admin · System Health";
        load();
        // Auto-refresh every 60s to catch SMTP/DB flakes early.
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, [load]);

    const ok = data?.ok;
    const CRITICAL_ENV_KEYS = useMemo(
        () => ["JWT_SECRET", "MONGO_URL", "DB_NAME", "SMTP_USER", "SMTP_PASSWORD"],
        [],
    );
    const envOk = useMemo(() => {
        if (!data?.env) return false;
        return CRITICAL_ENV_KEYS.every((k) => data.env[k]);
    }, [data, CRITICAL_ENV_KEYS]);
    const envEntries = useMemo(() => Object.entries(data?.env || {}), [data]);
    const dbEntries = useMemo(
        () => Object.entries(data?.database?.collections || {}),
        [data],
    );

    return (
        <div className="p-6 md:p-8 max-w-[1100px] mx-auto" data-testid="admin-system-health">
            <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
                <div>
                    <div className="overline !text-orange-400 mb-1">Diagnostics</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-50 tracking-tight flex items-center gap-3">
                        <Heartbeat size={28} className="text-orange-400" weight="fill" />
                        System Health
                    </h1>
                    <p className="text-sm text-slate-400 mt-1.5">
                        Live status of env vars, database, SMTP, and recent admin activity. Auto-refreshes every 60s.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {data && (
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono-alt ${
                            ok
                                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                                : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                        }`} data-testid="sh-overall-status">
                            <StatusDot ok={ok} />
                            {ok ? "All systems go" : "Attention required"}
                        </span>
                    )}
                    <button
                        onClick={sendTestAlert}
                        disabled={testAlertLoading}
                        className="btn-ghost !py-1.5 text-xs disabled:opacity-50"
                        data-testid="sh-send-test-alert-btn"
                        title="Send a test watchdog email to ADMIN_EMAIL to verify SMTP end-to-end"
                    >
                        <PaperPlaneTilt size={14} weight="bold" />
                        {testAlertLoading ? "Sending…" : "Send test alert"}
                    </button>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="btn-ghost !py-1.5 text-xs disabled:opacity-50"
                        data-testid="sh-refresh-btn"
                    >
                        <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} weight="bold" />
                        {loading ? "Checking…" : "Refresh"}
                    </button>
                </div>
            </div>

            {error && (
                <div className="tech-card p-4 border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm mb-4" data-testid="sh-error">
                    <Warning size={16} weight="fill" className="inline mr-2 -mt-0.5" />
                    {error}
                </div>
            )}

            {!data && !error && (
                <div className="tech-card p-6 text-center text-sm text-slate-500">
                    {loading ? "Running live checks…" : "Click Refresh to run diagnostics."}
                </div>
            )}

            {data && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <Section
                        icon={Key}
                        title="Environment variables"
                        ok={envOk}
                        subtitle="Only presence is shown — values are never exposed"
                        testid="sh-env-section"
                    >
                        {envEntries.map(([k, v]) => (
                            <KV key={k} k={k} v={v ? "set" : "missing"} ok={v} />
                        ))}
                    </Section>

                    <Section
                        icon={Database}
                        title="Database"
                        ok={data.database.reachable}
                        subtitle={data.database.reachable ? "MongoDB — live" : (data.database.error || "Not reachable")}
                        testid="sh-db-section"
                    >
                        {data.database.error && (
                            <div className="text-rose-300 text-xs font-mono-alt mb-2">{data.database.error}</div>
                        )}
                        {dbEntries.map(([name, n]) => (
                            <KV key={name} k={name} v={n === null ? "error" : `${Number(n).toLocaleString()} docs`} />
                        ))}
                    </Section>

                    <Section
                        icon={EnvelopeOpen}
                        title="SMTP — live handshake"
                        ok={data.smtp.connected}
                        subtitle={`${data.smtp.host}:${data.smtp.port}`}
                        testid="sh-smtp-section"
                    >
                        {data.smtp.error && (
                            <div className="text-rose-300 text-xs font-mono-alt mb-2 p-2 bg-rose-500/10 border border-rose-500/30 rounded">
                                {data.smtp.error}
                            </div>
                        )}
                        <KV k="connection" v={data.smtp.connected ? "OK" : "failed"} ok={data.smtp.connected} />
                        <KV k="round-trip" v={data.smtp.elapsed_ms != null ? `${data.smtp.elapsed_ms} ms` : "—"} />
                        <KV k="mode" v={data.smtp.port === 465 ? "SMTPS (SSL)" : "SMTP+STARTTLS"} />
                        <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                            A live SSL login test is performed on every refresh. If this fails, the campaign-send and forgot-password flows will also fail.
                        </p>
                    </Section>

                    <Section
                        icon={Lightning}
                        title="Recent activity"
                        testid="sh-activity-section"
                    >
                        <KV k="drip sends · last 7 days" v={data.recent.drip_sends_last_7d ?? 0} />
                        {data.recent.latest_campaign ? (
                            <>
                                <KV k="latest campaign · name" v={data.recent.latest_campaign.name} />
                                <KV k="latest campaign · status" v={data.recent.latest_campaign.status} />
                                <KV k="latest campaign · created" v={new Date(data.recent.latest_campaign.created_at).toLocaleString()} />
                            </>
                        ) : (
                            <KV k="latest campaign" v="none yet" />
                        )}
                        {data.recent.latest_inquiry && (
                            <>
                                <KV k="latest inquiry · from" v={data.recent.latest_inquiry.email} />
                                <KV k="latest inquiry · at" v={new Date(data.recent.latest_inquiry.created_at).toLocaleString()} />
                            </>
                        )}
                    </Section>
                </div>
            )}

            {data && (
                <div className="mt-6 text-[11px] text-slate-500 font-mono-alt flex items-center gap-1.5" data-testid="sh-footer-time">
                    <Clock size={12} /> Server time: {data.server_time_utc}
                </div>
            )}
        </div>
    );
}
